import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { initialState, VoivodeshipsState } from './voivodeships.state';
import * as voivodeshipsActions from './voivodeships.actions';

const reducer: ActionReducer<VoivodeshipsState, Action> = createReducer(
  initialState,
  on(
    voivodeshipsActions.getVoivodeships,
    (state): VoivodeshipsState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),

  on(voivodeshipsActions.getVoivodeshipsSuccess, (state, { voivodeships }): VoivodeshipsState => {
    const voivos = structuredClone(voivodeships);
    voivos.sort((voivoA, voivoB) => {
      const vA = voivoA.key.toLowerCase();
      const vB = voivoB.key.toLowerCase();
      if (vB < vA) {
        return 1;
      }
      return -1;
    });
    return {
      ...state,
      loading: false,
      errorMessage: null,
      availableVoivodeships: voivos,
    };
  }),

  on(
    voivodeshipsActions.getVoivodeshipsError,
    (state, { errorMessage }): VoivodeshipsState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    voivodeshipsActions.updateVoivodeship,
    voivodeshipsActions.addVoivodeshipOffice,
    voivodeshipsActions.editVoivodeshipOffice,
    voivodeshipsActions.removeVoivodeshipOffice,
    (state): VoivodeshipsState => ({ ...state, loading: true, errorMessage: null })
  ),
  on(voivodeshipsActions.updateVoivodeshipSuccess, (state, { updatedVoivodeship }): VoivodeshipsState => {
    return {
      ...state,
      availableVoivodeships: state.availableVoivodeships.map(voivoInState =>
        voivoInState.id === updatedVoivodeship.id ? { ...updatedVoivodeship } : voivoInState
      ),
      loading: false,
      errorMessage: null,
    };
  }),
  on(
    voivodeshipsActions.updateVoivodeshipError,
    voivodeshipsActions.addVoivodeshipOfficeError,
    voivodeshipsActions.editVoivodeshipOfficeError,
    voivodeshipsActions.removeVoivodeshipOfficeError,
    (state, { errorMessage }): VoivodeshipsState => ({ ...state, loading: false, errorMessage })
  ),

  on(voivodeshipsActions.addVoivodeshipOfficeSuccess, (state, { addedOffice, voivoId }): VoivodeshipsState => {
    return {
      ...state,
      availableVoivodeships: state.availableVoivodeships.map(voivoInState =>
        voivoInState.id === voivoId
          ? {
              ...voivoInState,
              offices: [...voivoInState.offices, addedOffice],
            }
          : voivoInState
      ),
      loading: false,
      errorMessage: null,
    };
  }),
  on(voivodeshipsActions.editVoivodeshipOfficeSuccess, (state, { updatedOffice, voivoId }): VoivodeshipsState => {
    return {
      ...state,
      availableVoivodeships: state.availableVoivodeships.map(voivoInState =>
        voivoInState.id === voivoId
          ? {
              ...voivoInState,
              offices: voivoInState.offices.map(office =>
                office.id === updatedOffice.id ? { ...updatedOffice } : office
              ),
            }
          : voivoInState
      ),
      loading: false,
      errorMessage: null,
    };
  }),
  on(voivodeshipsActions.removeVoivodeshipOfficeSuccess, (state, { officeId, voivoId }): VoivodeshipsState => {
    return {
      ...state,
      availableVoivodeships: state.availableVoivodeships.map(voivoInState =>
        voivoInState.id === voivoId
          ? { ...voivoInState, offices: voivoInState.offices.filter(office => office.id !== officeId) }
          : voivoInState
      ),
      loading: false,
      errorMessage: null,
    };
  }),

  on(
    voivodeshipsActions.searchDocuments,
    (state): VoivodeshipsState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    voivodeshipsActions.searchDocumentsSuccess,
    (state, { documents }): VoivodeshipsState => ({
      ...state,
      voivodeshipDocuments: documents,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    voivodeshipsActions.searchDocumentsError,
    (state, { errorMessage, opts }): VoivodeshipsState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    voivodeshipsActions.updateDocEnabled,
    voivodeshipsActions.updateDocEnabledBulk,
    (state): VoivodeshipsState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    voivodeshipsActions.updateDocEnabledSuccess,
    (state, { updatedDoc }): VoivodeshipsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      voivodeshipDocuments: state.voivodeshipDocuments.map(doc => (doc.id === updatedDoc.id ? { ...updatedDoc } : doc)),
    })
  ),
  on(
    voivodeshipsActions.updateDocEnabledBulkSuccess,
    (state, { updatedDocs }): VoivodeshipsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      voivodeshipDocuments: state.voivodeshipDocuments.map(doc => {
        const toReplace = updatedDocs.find(updatedDoc => updatedDoc.id === doc.id);
        return toReplace ? { ...toReplace } : { ...doc };
      }),
    })
  ),
  on(
    voivodeshipsActions.updateDocEnabledError,
    (state, { errorMessage }): VoivodeshipsState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    voivodeshipsActions.getDistricts,
    (state, { opts: { voivoId } }): VoivodeshipsState => ({
      ...state,
      loadingDistricts: true,
      loadedDistricts: { forVoivoId: voivoId, districts: [] },
    })
  ),
  on(
    voivodeshipsActions.getDistrictsSuccess,
    (state, { districts, voivoId }): VoivodeshipsState => ({
      ...state,
      loadingDistricts: false,
      loadedDistricts: { forVoivoId: voivoId, districts },
    })
  ),
  on(
    voivodeshipsActions.getMunicips,
    (state, { opts: { districtId } }): VoivodeshipsState => ({
      ...state,
      loadingMunicips: true,
      loadedMunicips: { forDistrictId: districtId, municips: [] },
    })
  ),
  on(
    voivodeshipsActions.getMunicipsSuccess,
    (state, { municips, districtId }): VoivodeshipsState => ({
      ...state,
      loadingMunicips: false,
      loadedMunicips: { forDistrictId: districtId, municips },
    })
  ),
  on(
    voivodeshipsActions.getCities,
    (state, { opts: { municipId } }): VoivodeshipsState => ({
      ...state,
      loadingCities: true,
      loadedCities: { forMunicipId: municipId, cities: [] },
    })
  ),
  on(
    voivodeshipsActions.getCitiesSuccess,
    (state, { municipId, cities }): VoivodeshipsState => ({
      ...state,
      loadingCities: false,
      loadedCities: { forMunicipId: municipId, cities },
    })
  ),
  on(
    voivodeshipsActions.downloadVoivodeshipFileInstruction,
    (state): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: true,
    })
  ),
  on(
    voivodeshipsActions.downloadVoivodeshipFileInstructionSuccess,
    (state): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: false,
    })
  ),
  on(
    voivodeshipsActions.downloadVoivodeshipFileInstructionError,
    (state): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: false,
    })
  ),
  on(
    voivodeshipsActions.uploadVoivodeshipFileInstruction,
    (state): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: true,
    })
  ),
  on(
    voivodeshipsActions.uploadVoivodeshipFileInstructionSuccess,
    (state, { availableVoivodeships }): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: false,
      availableVoivodeships,
    })
  ),
  on(
    voivodeshipsActions.uploadVoivodeshipFileInstructionError,
    (state): VoivodeshipsState => ({
      ...state,
      loadingVoivodeshipFileInstruction: false,
    })
  )
);

export function voivodeshipsReducer(state: VoivodeshipsState, action: Action): VoivodeshipsState {
  return reducer(state, action);
}
